import React, { FC, MouseEvent } from 'react';
import { styled } from 'styled-components';
import {
  Icon,
  faAnglesRight,
  faAnglesLeft,
  faChevronRight,
  faChevronDown,
  faCircle,
  faCircleCheck,
  faCircleHalfStroke,
  faFileCircleCheck,
  faFileCircleExclamation,
  faFileCircleQuestion,
  faFileCircleXmark,
  faTriangleExclamation,
} from '@/ui/components';
import { useTranslate } from '@/translations';
import { AuthorizationStatus, Damage, DamagePart } from '@/types';

export type PreTimeStatus =
  | 'notStarted'
  | 'complete'
  | 'partsMissing'
  | 'inProgress';
export type PreTimeTone = 'red' | 'green' | 'neutral';
export type FrIconState = 'ok' | 'missing';
export type FrPartsIconState = 'none' | 'frOnly' | 'both';
export type MaterialsIconState = 'ok' | 'missing';

const isMaterialPart = (type?: string) => {
  const normalized = type?.toLowerCase() ?? '';
  return normalized.includes('paint') || normalized.includes('fluid');
};

export const isPartsRequired = (damage: Pick<Damage, 'repairActionDesc'>) =>
  (damage.repairActionDesc || '').toLowerCase().includes('replace');

const countPartsByType = (parts: DamagePart[] = []) =>
  parts.reduce(
    (totals, part) => {
      if (isMaterialPart(part.type)) {
        totals.materialsCount += 1;
      } else {
        totals.partsCount += 1;
      }
      return totals;
    },
    { partsCount: 0, materialsCount: 0 },
  );

export const getPreTimeCounts = (damage: Damage) => {
  const actions = damage.actions || [];
  const { partsCount, materialsCount } = countPartsByType(
    actions.flatMap((action) => action.parts || []),
  );

  return {
    frCount: actions.filter(
      (action) => (action.type || '').toLowerCase() !== 'labour',
    ).length,
    partsCount,
    materialsCount,
  };
};

export const getFrIconState = (frCount: number): FrIconState =>
  frCount >= 1 ? 'ok' : 'missing';

export const getMaterialsIconState = (
  materialsCount: number,
): MaterialsIconState => (materialsCount >= 1 ? 'ok' : 'missing');

export const getFrPartsIconState = (
  frCount: number,
  partsCount: number,
  partsRequired: boolean,
): FrPartsIconState => {
  if (frCount === 0) return 'none';
  if (partsRequired && partsCount === 0) return 'frOnly';
  return 'both';
};

export const getPreTimeStatus = (damage: Damage): PreTimeStatus => {
  const { frCount, partsCount } = getPreTimeCounts(damage);
  if (!frCount) return 'notStarted';
  if (isPartsRequired(damage) && !partsCount) return 'partsMissing';
  if (!partsCount) return 'inProgress';
  return 'complete';
};

export type CollapsedPreTimeStatus = PreTimeStatus | 'approved' | 'rejected';

export const getCollapsedPreTimeStatus = (
  damage: Damage,
): CollapsedPreTimeStatus => {
  if (damage.authorizationStatusNum === AuthorizationStatus.Approved) {
    return 'approved';
  }
  if (damage.authorizationStatusNum === AuthorizationStatus.Rejected) {
    return 'rejected';
  }
  return getPreTimeStatus(damage);
};

export type PreTimeValidation = {
  frCount: number;
  partsCount: number;
  materialsCount: number;
  partsRequired: boolean;
  frTone: Exclude<PreTimeTone, 'neutral'>;
  partsTone: PreTimeTone;
  isRed: boolean;
  frIcon: FrIconState;
  frPartsIcon: FrPartsIconState;
  materialsIcon: MaterialsIconState;
};

export const getPreTimeValidation = (damage: Damage): PreTimeValidation => {
  const { frCount, partsCount, materialsCount } = getPreTimeCounts(damage);
  const partsRequired = isPartsRequired(damage);
  const frTone = frCount >= 1 ? 'green' : 'red';
  const partsTone = !partsRequired
    ? 'neutral'
    : partsCount >= 1
      ? 'green'
      : 'red';

  return {
    frCount,
    partsCount,
    materialsCount,
    partsRequired,
    frTone,
    partsTone,
    isRed: frTone === 'red' || partsTone === 'red',
    frIcon: getFrIconState(frCount),
    frPartsIcon: getFrPartsIconState(frCount, partsCount, partsRequired),
    materialsIcon: getMaterialsIconState(materialsCount),
  };
};

export type PreTimeMetric = {
  filled: number;
  required: number;
  tone: PreTimeTone;
};

export const getPreTimeProgress = (damage: Damage) => {
  const validation = getPreTimeValidation(damage);

  return {
    status: getPreTimeStatus(damage),
    frIcon: validation.frIcon,
    frPartsIcon: validation.frPartsIcon,
    materialsIcon: validation.materialsIcon,
    frLines: {
      filled: validation.frCount,
      required: 1,
      tone: validation.frTone,
    },
    parts: {
      filled: validation.partsCount,
      required: validation.partsRequired ? 1 : 0,
      tone: validation.partsTone,
    },
    materials: {
      filled: validation.materialsCount,
      required: 0,
      tone: 'neutral' as const,
    },
    missingParts: validation.partsTone === 'red' ? 1 : 0,
  };
};

export const getAggregateFrIconState = (damages: Damage[]): FrIconState =>
  damages.length > 0 &&
  damages.every((damage) => getPreTimeCounts(damage).frCount >= 1)
    ? 'ok'
    : 'missing';

export const getAggregateMaterialsIconState = (
  damages: Damage[],
): MaterialsIconState =>
  damages.some((damage) => getPreTimeCounts(damage).materialsCount >= 1)
    ? 'ok'
    : 'missing';

export const getAggregateFrPartsIconState = (
  damages: Damage[],
): FrPartsIconState => {
  const rows = damages.map((damage) => ({
    ...getPreTimeCounts(damage),
    partsRequired: isPartsRequired(damage),
  }));
  const isMissingParts = (row: (typeof rows)[number]) =>
    row.partsRequired && row.partsCount === 0;

  if (
    !rows.length ||
    rows.every((row) => row.frCount === 0 && row.partsCount === 0)
  ) {
    return 'none';
  }

  if (rows.some((row) => row.frCount >= 1 && isMissingParts(row))) {
    return 'frOnly';
  }

  if (
    rows.every((row) => row.frCount >= 1) &&
    rows.every((row) => !isMissingParts(row))
  ) {
    return 'both';
  }

  return rows.some((row) => row.frCount >= 1) ? 'both' : 'none';
};

export const getPreTimeAggregateStatus = (damages: Damage[]): PreTimeStatus => {
  if (
    !damages.length ||
    damages.every((damage) => getPreTimeStatus(damage) === 'notStarted')
  ) {
    return 'notStarted';
  }

  if (
    damages.some(
      (damage) =>
        isPartsRequired(damage) && getPreTimeCounts(damage).partsCount === 0,
    )
  ) {
    return 'partsMissing';
  }

  if (damages.every((damage) => getPreTimeStatus(damage) === 'complete')) {
    return 'complete';
  }

  return 'inProgress';
};

export type PreTimeAggregate = PreTimeValidation & { status: PreTimeStatus };

export const getPreTimeAggregate = (damages: Damage[]): PreTimeAggregate => {
  const rows = damages.map(getPreTimeValidation);
  const frCount = rows.reduce((sum, row) => sum + row.frCount, 0);
  const partsCount = rows.reduce((sum, row) => sum + row.partsCount, 0);
  const materialsCount = rows.reduce((sum, row) => sum + row.materialsCount, 0);
  const partsRequired = rows.some((row) => row.partsRequired);
  const frTone: Exclude<PreTimeTone, 'neutral'> =
    !rows.length || rows.some((row) => row.frTone === 'red') ? 'red' : 'green';
  const partsTone: PreTimeTone = rows.some((row) => row.partsTone === 'red')
    ? 'red'
    : partsRequired
      ? 'green'
      : 'neutral';
  const isRed = !rows.length || rows.some((row) => row.isRed);

  return {
    frCount,
    partsCount,
    materialsCount,
    partsRequired,
    frTone,
    partsTone,
    isRed,
    frIcon: getAggregateFrIconState(damages),
    frPartsIcon: getAggregateFrPartsIconState(damages),
    materialsIcon: getAggregateMaterialsIconState(damages),
    status: getPreTimeAggregateStatus(damages),
  };
};

export const PRE_TIME_STATUS_STYLES: Record<
  PreTimeStatus,
  { background: string; color: string; icon: typeof faCircle }
> = {
  notStarted: {
    background: '#f0f0f0',
    color: '#595959',
    icon: faCircle,
  },
  complete: {
    background: '#dcfce7',
    color: '#15803d',
    icon: faCircleCheck,
  },
  partsMissing: {
    background: '#ffedd5',
    color: '#c2410c',
    icon: faTriangleExclamation,
  },
  inProgress: {
    background: '#dbeafe',
    color: '#1d4ed8',
    icon: faCircleHalfStroke,
  },
};

export const preTimeStatusLabelKey = (status: PreTimeStatus) =>
  status === 'notStarted'
    ? 'notStarted'
    : status === 'complete'
      ? 'complete'
      : status === 'partsMissing'
        ? 'partsMissing'
        : 'inProgress';

export const COLLAPSED_PRE_TIME_STATUS_STYLES: Record<
  CollapsedPreTimeStatus,
  { background: string; color: string; icon: typeof faCircle }
> = {
  ...PRE_TIME_STATUS_STYLES,
  approved: {
    background: '#ecfdf3',
    color: '#12b76a',
    icon: faFileCircleCheck,
  },
  rejected: {
    background: '#fef3f2',
    color: '#d92d20',
    icon: faFileCircleXmark,
  },
};

export const collapsedPreTimeStatusLabelKey = (
  status: CollapsedPreTimeStatus,
) =>
  status === 'approved'
    ? 'legendApprovedDamage'
    : status === 'rejected'
      ? 'legendRejectedDamage'
      : preTimeStatusLabelKey(status);

export const PRE_TIME_FR_ICONS: Record<
  FrIconState,
  { src: string; label: string }
> = {
  ok: { src: '/images/Damage_FR_OK.png', label: 'FR OK' },
  missing: { src: '/images/Damage_FR_Missing.png', label: 'FR Missing' },
};

export const PRE_TIME_FR_PARTS_ICONS: Record<
  FrPartsIconState,
  { src: string; label: string }
> = {
  none: {
    src: '/images/Damage_No_FR_No_Parts.png',
    label: 'No FR / No Parts',
  },
  frOnly: {
    src: '/images/Damage_FR_OK_Parts_Missing.png',
    label: 'Only FR, no parts',
  },
  both: {
    src: '/images/Damage_FR_and_Parts_OK.png',
    label: 'FR + Parts OK',
  },
};

export const PRE_TIME_MATERIALS_ICONS: Record<
  MaterialsIconState,
  { src: string; label: string }
> = {
  ok: { src: '/images/Damage_Materials_OK.png', label: 'Materials OK' },
  missing: { src: '/images/Damage_Materials.png', label: 'Materials Missing' },
};

const TONE_COLOR: Record<PreTimeTone, string> = {
  red: '#dc2626',
  green: '#16a34a',
  neutral: '#434343',
};

const Header = styled.span`
  width: 100%;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 6px;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  width: 100%;
`;

const CountTag = styled.span<{ $tone?: PreTimeTone }>`
  width: ${({ $tone }) => ($tone ? '52px' : '86px')};
  height: 24px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 2px 8px;
  border-radius: 999px;
  background: #fff;
  border: 1px solid ${({ $tone }) => TONE_COLOR[$tone || 'neutral']};
  color: ${({ $tone }) => TONE_COLOR[$tone || 'neutral']};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;
`;

const ReviewButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  padding: 0;
  border: none;
  background: transparent;
  color: #d48806;
  font-size: 18px;
  cursor: pointer;
`;

const AuthorizationMark = styled.span<{ $color: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  color: ${({ $color }) => $color};
  font-size: 18px;
`;

const OpenButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  border: 0;
  background: transparent;
  color: #8c8c8c;
  cursor: pointer;

  &[aria-expanded='true'] {
    color: #1d4ed8;
  }
`;

const Content = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const StatusIcon = styled.span<{ $background: string; $color: string }>`
  width: 120px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  flex-shrink: 0;
  padding: 4px 10px;
  border-radius: 999px;
  background: ${({ $background }) => $background};
  color: ${({ $color }) => $color};
  border: 1px solid ${({ $color }) => $color};
  font-size: 12px;
  font-weight: 600;
  line-height: 1.2;
  white-space: nowrap;

  svg {
    width: 12px;
    height: 12px;
  }
`;

const MetricImg = styled.img`
  width: 20px;
  height: 20px;
  object-fit: contain;
  flex-shrink: 0;
`;

const CountTags: FC<{
  frCount: number;
  partsCount: number;
  materialsCount: number;
  frTone: PreTimeTone;
  partsTone: PreTimeTone;
  frIcon: FrIconState;
  frPartsIcon: FrPartsIconState;
  materialsIcon: MaterialsIconState;
}> = ({
  frCount,
  partsCount,
  materialsCount,
  frTone,
  partsTone,
  frIcon,
  frPartsIcon,
  materialsIcon,
}) => {
  const fr = PRE_TIME_FR_ICONS[frIcon];
  const parts = PRE_TIME_FR_PARTS_ICONS[frPartsIcon];
  const materials = PRE_TIME_MATERIALS_ICONS[materialsIcon];

  return (
    <>
      <CountTag $tone={frTone} data-testid="pre-time-fr">
        <MetricImg src={fr.src} alt={fr.label} title={fr.label} />
        {frCount}
      </CountTag>
      <CountTag $tone={partsTone} data-testid="pre-time-parts">
        <MetricImg src={parts.src} alt={parts.label} title={parts.label} />
        {partsCount}
      </CountTag>
      <CountTag $tone="neutral" data-testid="pre-time-materials">
        <MetricImg
          src={materials.src}
          alt={materials.label}
          title={materials.label}
        />
        {materialsCount}
      </CountTag>
    </>
  );
};

const StatusIconBadge: FC<{ status: PreTimeStatus }> = ({ status }) => {
  const { t } = useTranslate();
  const style = PRE_TIME_STATUS_STYLES[status];
  const label = t(preTimeStatusLabelKey(status));

  return (
    <StatusIcon
      $background={style.background}
      $color={style.color}
      data-testid="pre-time-status"
      data-pt-status={status}
    >
      <Icon icon={style.icon} />
      {label}
    </StatusIcon>
  );
};

const PreTimeMetricsWrap = styled.div`
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px 0;
  min-width: 0;
  color: #434343;
  font-size: 13px;
`;

const PreTimeDivider = styled.span`
  width: 1px;
  height: 16px;
  margin: 0 12px;
  background: #d9d9d9;
`;

const PreTimeMetricItem = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  white-space: nowrap;
  color: #434343;

  img {
    width: 20px;
    height: 20px;
    object-fit: contain;
  }
`;

const PreTimeLabel = styled.span`
  margin-right: 8px;
  color: #8c8c8c;
  font-weight: 600;
`;

export const PreTimeMetrics: FC<{
  validation: Pick<
    PreTimeValidation,
    | 'frCount'
    | 'partsCount'
    | 'materialsCount'
    | 'frIcon'
    | 'frPartsIcon'
    | 'materialsIcon'
  > & { status: PreTimeStatus };
}> = ({ validation }) => {
  const { t } = useTranslate();
  const fr = PRE_TIME_FR_ICONS[validation.frIcon];
  const parts = PRE_TIME_FR_PARTS_ICONS[validation.frPartsIcon];
  const materials = PRE_TIME_MATERIALS_ICONS[validation.materialsIcon];

  return (
    <PreTimeMetricsWrap data-testid="pre-time-metrics">
      <PreTimeLabel>{t('preTime')}:</PreTimeLabel>
      <StatusIconBadge status={validation.status} />
      <PreTimeDivider />
      <PreTimeMetricItem>
        {`${t('frLines')}: ${validation.frCount}`}
        <img src={fr.src} alt={fr.label} title={fr.label} />
      </PreTimeMetricItem>
      <PreTimeDivider />
      <PreTimeMetricItem>
        {`${t('parts')}: ${validation.partsCount}`}
        <img src={parts.src} alt={parts.label} title={parts.label} />
      </PreTimeMetricItem>
      <PreTimeDivider />
      <PreTimeMetricItem>
        {`${t('materials')}: ${validation.materialsCount}`}
        <img
          src={materials.src}
          alt={materials.label}
          title={materials.label}
        />
      </PreTimeMetricItem>
    </PreTimeMetricsWrap>
  );
};

interface PreTimeColumnTitleProps {
  expanded?: boolean;
  onToggle: () => void;
}

export const PreTimeColumnTitle: FC<PreTimeColumnTitleProps> = ({
  expanded,
  onToggle,
}) => {
  const { t } = useTranslate();

  const handleToggle = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onToggle();
  };

  return (
    <Header>
      {expanded ? t('preTime') : t('pt')}
      <OpenButton
        type="button"
        aria-label={expanded ? t('collapse') : t('expand')}
        aria-expanded={!!expanded}
        onClick={handleToggle}
      >
        <Icon icon={expanded ? faAnglesLeft : faAnglesRight} />
      </OpenButton>
    </Header>
  );
};

interface PreTimeCellProps {
  damage: Damage;
  expanded?: boolean;
  open?: boolean;
  reviewRequired?: boolean;
  amended?: boolean;
  onOpenDetail: () => void;
  onReview?: () => void;
}

export const PreTimeCell: FC<PreTimeCellProps> = ({
  damage,
  expanded,
  open = false,
  reviewRequired = false,
  amended = false,
  onOpenDetail,
  onReview,
}) => {
  const { t } = useTranslate();
  const validation = getPreTimeValidation(damage);
  const status = getPreTimeStatus(damage);
  const collapsedStatus = getCollapsedPreTimeStatus(damage);
  const collapsedStyle = COLLAPSED_PRE_TIME_STATUS_STYLES[collapsedStatus];

  const handleOpen = (event: MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();
    onOpenDetail();
  };

  return (
    <Row data-testid="pre-time-cell">
      <Content>
        {expanded ? (
          <>
            <StatusIconBadge status={status} />
            <CountTags
              frCount={validation.frCount}
              partsCount={validation.partsCount}
              materialsCount={validation.materialsCount}
              frTone={validation.frTone}
              partsTone={validation.partsTone}
              frIcon={validation.frIcon}
              frPartsIcon={validation.frPartsIcon}
              materialsIcon={validation.materialsIcon}
            />
          </>
        ) : (
          <>
            <AuthorizationMark
              role="img"
              $color={collapsedStyle.color}
              aria-label={t(collapsedPreTimeStatusLabelKey(collapsedStatus))}
              data-testid="pre-time-status-icon"
              data-pt-status={collapsedStatus}
            >
              <Icon icon={collapsedStyle.icon} />
            </AuthorizationMark>
            {reviewRequired && onReview ? (
              <ReviewButton
                type="button"
                aria-label={t('ptReviewRequired')}
                onClick={(event) => {
                  event.stopPropagation();
                  onReview();
                }}
              >
                <Icon icon={faFileCircleExclamation} />
              </ReviewButton>
            ) : null}
            {reviewRequired && !onReview ? (
              <AuthorizationMark
                role="img"
                $color="#d48806"
                aria-label={t('ptReviewRequired')}
                data-testid="pre-time-review-required-icon"
              >
                <Icon icon={faFileCircleExclamation} />
              </AuthorizationMark>
            ) : null}
            {amended ? (
              <AuthorizationMark
                role="img"
                $color="#d48806"
                aria-label={t('amendedSinceLastReview')}
                data-testid="damage-amended-mark"
              >
                <Icon icon={faFileCircleQuestion} />
              </AuthorizationMark>
            ) : null}
          </>
        )}
      </Content>
      <OpenButton
        type="button"
        aria-label={t('details')}
        aria-expanded={open}
        onClick={handleOpen}
      >
        <Icon icon={open ? faChevronDown : faChevronRight} />
      </OpenButton>
    </Row>
  );
};
